import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, SearchX } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { SelectedListingPreview } from '@/components/advertise/SelectedListingPreview';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, sellersStepValid } from '@/utils/advertiseGuards';
import { SELLERS_STEPS } from '@/config/advertiseSteps';
import { getAllListings } from '@/services/api';
import { ADVERTISE_SELLERS } from '@/constants/testIds/advertise';

export default function SellersVerify() {
  const navigate = useNavigate();
  const sellers = useAdvertise((s) => s.sellers);
  const setField = useAdvertise((s) => s.setField);
  useStepGuard(sellersStepValid.verify(sellers), '/advertise/sellers');

  const [searching, setSearching] = useState(true);
  const [manual, setManual] = useState(!sellers.matchedListingId && !!sellers.listPrice);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const houseNumber = sellers.address.match(/^\d+/)?.[0];
    getAllListings({ filters: { city: sellers.city, zip: sellers.zip, status: ['A', 'U'] }, pageSize: 50 })
      .then(({ items }) => {
        if (cancelled) return;
        const candidates = houseNumber ? items.filter((l) => l.streetAddress?.startsWith(houseNumber)) : [];
        if (candidates.length === 1) {
          setField('sellers', { matchedListingId: candidates[0].id, matchedListing: candidates[0] });
          setManual(false);
        } else {
          setManual(true);
        }
      })
      .catch(() => setManual(true))
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setField('sellers', patch);
  const useManualEntry = () => { set({ matchedListingId: null, matchedListing: null }); setManual(true); };

  const canProceed = manual ? !!sellers.listPrice : !!sellers.matchedListingId;

  return (
    <AdvertiseLayout
      trackLabel="Listing Boost"
      steps={SELLERS_STEPS}
      currentStepId="verify"
      title="Confirm your home"
      subtitle="Matching your address to an active listing…"
      sidebar={<AdvertiseSideSummary items={[{ label: 'Address', value: sellers.address }]} />}
      footer={<AdvertiseFooterNav onBack={() => navigate('/advertise/sellers/address')} onNext={() => navigate('/advertise/sellers/details')} nextDisabled={!canProceed} />}
    >
      {searching ? (
        <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" />Searching active listings…
        </div>
      ) : !manual ? (
        <div>
          <SelectedListingPreview listing={sellers.matchedListing} />
          <div className="mt-5 flex flex-wrap gap-3">
            <Button type="button" onClick={() => navigate('/advertise/sellers/details')} className="rounded-full" data-testid={ADVERTISE_SELLERS.verifyConfirm}>This is my home</Button>
            <Button type="button" variant="outline" onClick={useManualEntry} className="rounded-full" data-testid={ADVERTISE_SELLERS.verifyNotMine}>Not my home — enter manually</Button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <SearchX size={16} className="shrink-0" />We couldn&rsquo;t find a matching active listing — enter your home&rsquo;s details below.
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div><Label>Beds</Label><Input type="number" value={sellers.beds} onChange={(e) => set({ beds: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sellers-verify-beds" /></div>
            <div><Label>Baths</Label><Input type="number" value={sellers.baths} onChange={(e) => set({ baths: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sellers-verify-baths" /></div>
            <div><Label>Sqft</Label><Input type="number" value={sellers.sqft} onChange={(e) => set({ sqft: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sellers-verify-sqft" /></div>
            <div><Label>List price</Label><Input type="number" value={sellers.listPrice} onChange={(e) => set({ listPrice: e.target.value })} placeholder="450000" className="mt-1.5 rounded-xl" data-testid="sellers-verify-list-price" /></div>
          </div>
        </div>
      )}
    </AdvertiseLayout>
  );
}
